/**
 * ECIES (Elliptic Curve Integrated Encryption Scheme) Service
 *
 * Handles authenticated ECIES decryption for unwrapping the DEK.
 *
 * Security Architecture - Authenticated ECIES with TWO shared secrets:
 *
 * During key exchange, the consultant wraps the DEK using:
 * - ss1 = ECDH(ephemeralPrivate, customerPublic) - Forward secrecy
 * - ss2 = ECDH(consultantStaticPrivate, customerPublic) - Authentication
 *
 * To unwrap, the customer computes:
 * - ss1 = ECDH(customerPrivate, ephemeralPublic) - Same shared secret
 * - ss2 = ECDH(customerPrivate, consultantStaticPublic) - Verifies consultant identity
 *
 * The two shared secrets prevent MITM attacks. If an attacker substitutes
 * their own key, ss2 computation will produce a different result and
 * decryption will fail.
 */

import { x25519 } from '@noble/curves/ed25519.js'
import { hkdf } from '@noble/hashes/hkdf.js'
import { sha256 } from '@noble/hashes/sha2.js'

import type { CustomerWrappedDEK, CustomerKeyPair } from '@/types/crypto'
import { HKDF_INFO, AES_CONSTANTS } from '@/types/crypto'
import { base64ToUint8Array } from './crypto.service'

/**
 * ECIES Service for Customer DEK Unwrapping
 */
class ECIESService {
  /**
   * Unwrap DEK using authenticated ECIES
   *
   * Uses TWO shared secrets to verify consultant identity and prevent MITM attacks:
   *
   * 1. ss1 = ECDH(customerPrivate, ephemeralPublic) - Forward secrecy
   * 2. ss2 = ECDH(customerPrivate, consultantStaticPublic) - Verifies consultant identity
   * 3. combinedSecret = ss1 || ss2
   * 4. derivedKey = HKDF-SHA256(combinedSecret, ephemeralPublicKey, "CustomerDEK-ECIES-v1")
   * 5. DEK = AES-256-GCM-Decrypt(derivedKey, ciphertext)
   *
   * If the consultant's static public key doesn't match the one used during
   * encryption (e.g., due to MITM attack), ss2 will be different and
   * decryption will fail with an authentication error.
   *
   * @param wrappedDEK - ECIES-wrapped DEK from the consultant
   * @param customerKeyPair - Customer's X25519 key pair (derived from passphrase)
   * @returns Unwrapped 256-bit DEK
   * @throws Error if decryption fails (wrong passphrase, tampering, or MITM attack)
   */
  async unwrapDEK(wrappedDEK: CustomerWrappedDEK, customerKeyPair: CustomerKeyPair): Promise<Uint8Array> {
    // Decode ephemeral public key (generated by consultant for forward secrecy)
    const ephemeralPublicKey = base64ToUint8Array(wrappedDEK.ephemeralPublicKey)

    // Decode consultant's static public key (for authentication)
    const consultantStaticPublicKey = base64ToUint8Array(wrappedDEK.consultantStaticPublicKey)

    // Compute TWO shared secrets for authenticated ECIES
    // ss1: Ephemeral shared secret (provides forward secrecy)
    const ss1 = x25519.getSharedSecret(customerKeyPair.privateKey, ephemeralPublicKey)

    // ss2: Static shared secret (verifies consultant identity)
    // If this doesn't match what the consultant computed, decryption will fail
    const ss2 = x25519.getSharedSecret(customerKeyPair.privateKey, consultantStaticPublicKey)

    // Combine both shared secrets (same order as encryption)
    const combinedSecret = new Uint8Array(ss1.length + ss2.length)
    combinedSecret.set(ss1, 0)
    combinedSecret.set(ss2, ss1.length)

    // Derive decryption key using HKDF-SHA256
    // Include ephemeral public key as salt for domain separation
    const hkdfInfo = new TextEncoder().encode(HKDF_INFO)
    const derivedKey = hkdf(sha256, combinedSecret, ephemeralPublicKey, hkdfInfo, 32)

    // Import derived key for AES-GCM decryption
    const aesKey = await crypto.subtle.importKey(
      'raw',
      derivedKey as BufferSource,
      { name: AES_CONSTANTS.ALGORITHM, length: AES_CONSTANTS.KEY_LENGTH },
      false,
      ['decrypt']
    )

    // Decode IV and ciphertext
    const iv = base64ToUint8Array(wrappedDEK.iv)
    const encryptedDEK = base64ToUint8Array(wrappedDEK.encryptedDEK)

    // Convert to ArrayBuffer for Web Crypto API
    const ivBuffer = this.toArrayBuffer(iv)
    const encryptedBuffer = this.toArrayBuffer(encryptedDEK)

    // Decrypt DEK with AES-256-GCM
    // If consultant identity doesn't match (wrong static key), this will fail
    // with a DOMException indicating "OperationError" (authentication failure)
    try {
      const decrypted = await crypto.subtle.decrypt(
        {
          name: AES_CONSTANTS.ALGORITHM,
          iv: ivBuffer,
        },
        aesKey,
        encryptedBuffer
      )

      // Securely clear intermediate secrets
      this.secureClear(combinedSecret)
      this.secureClear(derivedKey)

      return new Uint8Array(decrypted)
    } catch (error) {
      // Securely clear intermediate secrets even on failure
      this.secureClear(combinedSecret)
      this.secureClear(derivedKey)

      // Re-throw with more descriptive error
      if (error instanceof DOMException && error.name === 'OperationError') {
        throw new Error(
          'Failed to unwrap DEK: Authentication failed. ' +
            'This may indicate a wrong passphrase, data tampering, or consultant identity mismatch.'
        )
      }
      throw error
    }
  }

  /**
   * Verify consultant's static public key
   *
   * Before unwrapping, the customer can verify that the consultant's
   * static public key matches their expected consultant.
   *
   * @param wrappedDEK - Wrapped DEK containing consultant's public key
   * @param expectedConsultantPublicKey - Expected consultant's public key (Base64)
   * @returns True if the keys match
   */
  verifyConsultantPublicKey(wrappedDEK: CustomerWrappedDEK, expectedConsultantPublicKey: string): boolean {
    // Decode both keys
    const receivedKey = base64ToUint8Array(wrappedDEK.consultantStaticPublicKey)
    const expectedKey = base64ToUint8Array(expectedConsultantPublicKey)

    // Length check
    if (receivedKey.length !== expectedKey.length) {
      return false
    }

    // Constant-time comparison to prevent timing attacks
    let result = 0
    for (let i = 0; i < receivedKey.length; i++) {
      result |= receivedKey[i] ^ expectedKey[i]
    }
    return result === 0
  }

  /**
   * Extract consultant's static public key from wrapped DEK
   *
   * Useful for displaying to the user or logging for verification.
   *
   * @param wrappedDEK - Wrapped DEK containing consultant's public key
   * @returns Consultant's public key as Base64
   */
  getConsultantPublicKey(wrappedDEK: CustomerWrappedDEK): string {
    return wrappedDEK.consultantStaticPublicKey
  }

  /**
   * Securely clear sensitive data from memory
   *
   * @param data - Uint8Array to clear
   */
  private secureClear(data: Uint8Array): void {
    crypto.getRandomValues(data) // Overwrite with random data first
    data.fill(0) // Then fill with zeros
  }

  /**
   * Convert Uint8Array to ArrayBuffer (for Web Crypto API compatibility)
   *
   * This handles the case where Uint8Array.buffer could be SharedArrayBuffer
   *
   * @param data - Uint8Array to convert
   * @returns ArrayBuffer copy of the data
   */
  private toArrayBuffer(data: Uint8Array): ArrayBuffer {
    const buffer = new ArrayBuffer(data.byteLength)
    new Uint8Array(buffer).set(data)
    return buffer
  }
}

// Export singleton instance
export const eciesService = new ECIESService()
